import { ContentSwitcher, IconButton, Text } from "@carbon6/c6-frontend-design";
import { useState, type ReactNode } from "react";
import { canShowJobElapsed, type WorkerCurrentJob } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { workerJobStatusLabel } from "./workerJobStatusLabel";
import { WorkerStatusBadge } from "./WorkerStatusBadge";
import type { WorkerStatusTableRow } from "./WorkerStatusTableRow";
import {
  formatDiskCapacity,
  formatDiskUsage,
  formatCurrentJobKind,
  formatElapsedSince,
  formatJobProgress,
  formatWorkerAssignments,
  formatWorkerStorageBackends,
  formatLoadAverages,
  formatReportedAge,
  formatReportedAtAbsolute,
  formatScratchEntryAge,
} from "./workerStatusFormatters";
import { formatWorkerUnit } from "./workUnitFormat";
import type { WorkerJobTarget } from "./useWorkerJobDetailController";
import {
  WorkerDetailFrame,
  WorkerMetric,
  WorkerDetailSection,
  WorkerDetailField,
} from "./WorkerDetailContent";
import styles from "./WorkerDetailContent.module.css";

// 상세 모달이 있는 작업만 대상을 만든다. 통합·정리 같은 kind 는 null 이다.
export function workerJobTarget(job: WorkerCurrentJob): WorkerJobTarget | null {
  if (job.kind === "upload") return { kind: "upload", uuid: job.uuid };
  if (job.kind === "diagnostics") return { kind: "diagnostic", uuid: job.uuid };
  return null;
}

export type WorkerDetailModalProps = {
  // 열려 있는 워커의 id. 닫혀 있으면 null.
  workerId: string | null;
  // 지금 스냅샷의 행. 페이지가 폴링마다 다시 찾아 전달한다. 그래서 상세도 함께 갱신된다.
  // 목록에서 사라진 워커(Live 필터·보존 윈도우 이탈)는 null 이다.
  row: WorkerStatusTableRow | null;
  onClose: () => void;
  // 작업 상세를 연다. 페이지가 이 모달을 잠시 닫고 작업 모달을 연다. 두 모달을 겹치지 않는다.
  onOpenJob: (target: WorkerJobTarget) => void;
};

// 워커 하나의 heartbeat status 전체. 목록 컬럼이 폭 때문에 생략한 값(전체 uuid, 절대
// 시각, 디스크 절대 용량, 5m/15m load, scratch 경로와 항목별 나이)을 모두 표시한다. 데이터는
// 목록 응답에 이미 들어 있다. 그래서 상세를 열 때 추가 요청이 없다.
export function WorkerDetailModal({
  workerId,
  row,
  onClose,
  onOpenJob,
}: WorkerDetailModalProps): ReactNode {
  return (
    <WorkerDetailFrame
      open={workerId !== null}
      title={renderTitle(workerId, row)}
      onClose={onClose}
    >
      {row === null ? (
        renderMissingNotice()
      ) : (
        <WorkerDetailBody key={workerId} row={row} onOpenJob={onOpenJob} />
      )}
    </WorkerDetailFrame>
  );
}

function renderTitle(workerId: string | null, row: WorkerStatusTableRow | null): ReactNode {
  return (
    <span className={styles.status}>
      <span className={styles.mono}>{workerId ?? ""}</span>
      {row === null ? null : <WorkerStatusBadge online={row.online} />}
    </span>
  );
}

// 보던 워커가 목록에서 빠지는 경우가 실제로 있다. 예: Live 필터를 켠 상태에서 워커가 죽거나,
// 보존 윈도우(12h)를 넘긴다. 창을 알림 없이 닫지 않고, 무슨 일이 있었는지 알린다.
function renderMissingNotice(): ReactNode {
  return (
    <EmptyState>
      이 워커는 현재 목록에 없습니다. Live 필터를 끄면 최근 12시간 안에 보고한 워커에서 다시 찾을 수
      있습니다.
    </EmptyState>
  );
}

function WorkerDetailBody({
  row,
  onOpenJob,
}: {
  row: WorkerStatusTableRow;
  onOpenJob: (target: WorkerJobTarget) => void;
}): ReactNode {
  const [tab, setTab] = useState<"overview" | "resources">("overview");
  return (
    <div className={styles.content}>
      <div className={styles.metrics}>
        <WorkerMetric
          label="작업"
          value={row.current_job ? formatCurrentJobKind(row.current_job) : "대기 중"}
        />
        <WorkerMetric label="마지막 보고" value={formatReportedAge(row.age_seconds)} />
        <WorkerMetric label="Scratch 사용률" value={formatDiskUsage(row.disk)} />
      </div>
      <ContentSwitcher
        label="Worker 상세 정보"
        value={tab}
        onValueChange={(next) => setTab(next === "resources" ? "resources" : "overview")}
        options={[
          { value: "overview", label: "개요" },
          { value: "resources", label: "리소스·Scratch" },
        ]}
      />
      {tab === "overview" ? (
        <div className={styles.sections}>
          {renderSection(
            row.online ? "현재 작업" : "마지막 보고 작업",
            renderCurrentJob(row, onOpenJob),
          )}
          <div className={styles.content}>
            {renderSection(
              "담당",
              <>
                {renderField("큐", formatWorkerAssignments(row.queues, "없음"))}
                {renderField("저장 위치", formatWorkerStorageBackends(row.storageBackends))}
              </>,
            )}
            {renderSection(
              "보고",
              <>
                {renderField("보고 시각", formatReportedAtAbsolute(row.reported_at))}
                {renderField(
                  "마지막 진전 시각",
                  row.last_progress_at === null
                    ? "미보고"
                    : formatReportedAtAbsolute(row.last_progress_at),
                )}
                <p className={styles.note}>
                  진전 시각은 진행 표시값입니다. Worker 생존과 점유 만료를 뜻하지 않습니다.
                </p>
              </>,
            )}
          </div>
        </div>
      ) : (
        <div className={styles.sections}>
          {renderSection(
            "Scratch disk",
            <>
              {renderField("사용량", formatDiskCapacity(row.disk))}
              {renderField("경로", row.scratch_dir ?? "—", { mono: true })}
            </>,
          )}
          {renderSection("Load average", renderField("평균", formatLoadAverages(row.loadavg)))}
          {renderSection(
            `Scratch 항목 (${row.scratch_entry_count ?? row.scratch_entries.length})`,
            renderScratchEntries(row),
          )}
        </div>
      )}
    </div>
  );
}

// 점유 경과 표시 여부는 canShowJobElapsed 가 판정한다. offline 워커의 current_job 은 마지막으로
// 처리하던 job 이다. 경과를 계속 늘리면 죽은 워커가 일하는 것처럼 보인다(목록 셀과 같은 규약).
function renderCurrentJob(
  row: WorkerStatusTableRow,
  onOpenJob: (target: WorkerJobTarget) => void,
): ReactNode {
  const job = row.current_job;
  if (job === null) {
    return (
      <EmptyState size="sm" mode="plain">
        {row.online ? "지금은 처리 중인 작업이 없습니다." : "마지막 보고에 작업이 없습니다."}
      </EmptyState>
    );
  }
  const elapsed = canShowJobElapsed(row) ? formatElapsedSince(job.started_at, Date.now()) : null;
  const target = workerJobTarget(job);
  return (
    <>
      {renderField("종류", formatCurrentJobKind(job))}
      {renderField("Dataset", job.subject?.dataset_display_name ?? "—")}
      {renderField("작업 단위", job.subject?.unit ? formatWorkerUnit(job.subject.unit) : "—")}
      {renderField("상태", workerJobStatusLabel(job.state ?? "-"))}
      {renderField("점유 세대", job.generation == null ? "미보고" : String(job.generation))}
      {renderField("진행량", formatJobProgress(job.progress))}
      {renderField("uuid", job.uuid, { mono: true })}
      {renderField(
        row.online ? "점유 시각" : "마지막 점유 시각",
        [formatReportedAtAbsolute(job.started_at), elapsed ? `(${elapsed} 처리 중)` : null]
          .filter((part) => part !== null)
          .join(" "),
      )}
      {target !== null && (
        <IconButton
          text="작업 상세 열기"
          size="sm"
          variant="surface"
          onClick={() => onOpenJob(target)}
        />
      )}
    </>
  );
}

// scratch 항목은 이름과 나이만 있는 짧은 목록이다. 그래서 테이블 대신 정의형 행으로 나열한다.
// 워커가 이름 오름차순으로 정렬해 보낸 순서를 그대로 유지한다.
function renderScratchEntries(row: WorkerStatusTableRow): ReactNode {
  if (row.scratch_entries.length === 0) {
    return (
      <EmptyState size="sm" mode="plain">
        scratch 디렉터리가 비어 있습니다.
      </EmptyState>
    );
  }
  return (
    <>
      {row.scratch_entries.map((entry) => (
        <div key={entry.name} className={styles.scratchEntry}>
          <Text variant={hatcheryTextModes["caption-normal"]} className={styles.mono}>
            {entry.name}
          </Text>
          <Text variant={hatcheryTextModes["body-normal"]}>
            {formatScratchEntryAge(entry.age_seconds)}
          </Text>
        </div>
      ))}
    </>
  );
}

function renderSection(title: string, body: ReactNode): ReactNode {
  return <WorkerDetailSection title={title}>{body}</WorkerDetailSection>;
}

function renderField(label: string, value: string, options: { mono?: boolean } = {}): ReactNode {
  return (
    <WorkerDetailField label={label} mono={options.mono}>
      {value}
    </WorkerDetailField>
  );
}
